//
// Site JS
//

// Import CookieConsentConfig
import './components/cookie-consent-config';

// Import GSAP and plugins
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

// Remove .no-js and .preload classes
document.body.classList.remove('no-js');
document.body.classList.remove('preload');

// CONFIGURATION
const CONFIG = {
    ANIMATION: {
        MENU_DURATION: 0.2,
        FADE_DURATION: 2,
        STAGGER_DURATION: 0.15,
        STAGGER_ITEM_DURATION: 0.6,
        PARALLAX_DURATION: 0.6,
        CURSOR_SCALE: 0.7,
        CURSOR_SHOW_DURATION: 0.3,
    },
    SCROLL_TRIGGER: {
        FADE_START: 'top 80%',
        FADE_END: 'bottom 20%',
        SCROLL_START: 'top -20px',
        SHOWCASE_GAP: 40,
    },
    RESIZE: {
        DEBOUNCE_MS: 120,
        SCROLL_REFRESH_MS: 100,
    }
};

// UTILITY FUNCTIONS

// Get current viewport height, accounting for mobile browsers
const getViewportHeight = () => {
    return window.visualViewport ? window.visualViewport.height : window.innerHeight;
};

// Get current viewport width
const getViewportWidth = () => {
    return window.visualViewport ? window.visualViewport.width : window.innerWidth;
};

// Update CSS custom property for viewport height
const updateViewportHeight = () => {
    document.documentElement.style.setProperty('--vh', `${getViewportHeight()}px`);
};

// Set up viewport height tracking with proper event listener management
const initViewportTracking = () => {
    updateViewportHeight();

    let resizeTimeout;
    const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
            updateViewportHeight();
            ScrollTrigger.refresh();
        }, CONFIG.RESIZE.DEBOUNCE_MS);
    };

    window.addEventListener('resize', handleResize);
    if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
    }

    return () => {
        window.removeEventListener('resize', handleResize);
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', handleResize);
        }
    };
};

// MENU MANAGEMENT

// Menu state management class
class MenuManager {
    constructor() {
        this.isOpen = false;
        this.isAnimating = false;
        this.siteHeader = document.querySelector('#js-s-header');
        this.menuControl = document.querySelector('#js-menu-control');
        this.menuContent = document.querySelector('#js-menu-content');
        this.timeline = null;
        this.clickHandler = null;

        if (this.menuControl && this.menuContent) {
            this.init();
        }
    }

    init() {
        // Remove old listener if it exists
        if (this.clickHandler && this.menuControl) {
            this.menuControl.removeEventListener('click', this.clickHandler);
        }

        // Create new bound handler
        this.clickHandler = () => this.toggle();
        if (this.menuControl) {
            this.menuControl.addEventListener('click', this.clickHandler);
        }
    }

    toggle() {
        if (this.isAnimating) return;
        this.isOpen ? this.close() : this.open();
    }

    open() {
        if (this.isOpen || this.isAnimating) return;
        this.isAnimating = true;
        this.isOpen = true;

        this.timeline = gsap.timeline({
            onComplete: () => { this.isAnimating = false; }
        })
            .to(this.menuControl, { attr: { 'aria-expanded': true } }, 0)
            .to(this.menuContent, {
                duration: CONFIG.ANIMATION.MENU_DURATION,
                ease: 'power.out',
                height: 'auto'
            }, 0);
    }

    close() {
        if (!this.isOpen || this.isAnimating) return;
        this.isAnimating = true;
        this.isOpen = false;

        this.timeline = gsap.timeline({
            onComplete: () => { this.isAnimating = false; }
        })
            .to(this.menuControl, { attr: { 'aria-expanded': false } }, 0)
            .to(this.menuContent, {
                duration: CONFIG.ANIMATION.MENU_DURATION,
                ease: 'power.out',
                height: 0
            }, 0);
    }

    kill() {
        if (this.timeline) this.timeline.kill();
        if (this.clickHandler && this.menuControl) {
            this.menuControl.removeEventListener('click', this.clickHandler);
        }
    }

    reset() {
        this.isOpen = false;
        this.isAnimating = false;
        if (this.timeline) this.timeline.kill();
    }
}

// SCROLL HEADER

// Handle header scroll state
const initScrollHeader = () => {
    const siteHeader = document.querySelector('#js-s-header');
    if (!siteHeader) return null;

    ScrollTrigger.create({
        trigger: document.documentElement,
        start: CONFIG.SCROLL_TRIGGER.SCROLL_START,
        end: 99999,
        onEnter: () => siteHeader.classList.add('s-header--scrolled'),
        onLeaveBack: () => siteHeader.classList.remove('s-header--scrolled'),
    });

    return () => {
        ScrollTrigger.getAll().forEach(trigger => {
            if (trigger.vars.trigger === document.documentElement) {
                trigger.kill();
            }
        });
    };
};

document.addEventListener('DOMContentLoaded', function () {

    // Initialize viewport height tracking with cleanup
    const cleanupViewport = initViewportTracking();

    // Initialize menu with state management
    let menuManager = new MenuManager();

    // Initialize scroll header effect
    const cleanupScrollHeader = initScrollHeader();


    // HOMEPAGE ANIMATION

    const openingStatementCircle = document.querySelector('.h-opening-statement__circle');
    let cleanupOpeningStatement = null;

    if (openingStatementCircle) {
        const fullstop = document.querySelector('.h-opening-statement__fullstop');

        // Calculate clip-path position for opening statement circle
        const getInitialRadius = () => {
            if (!fullstop) return;

            const circleRect = openingStatementCircle.getBoundingClientRect();
            const fullstopRect = fullstop.getBoundingClientRect();
            const dotWidth = fullstopRect.width;
            const dotHeight = fullstopRect.height;

            const x = ((fullstopRect.left + dotWidth / 2 - circleRect.left) / circleRect.width) * 100;
            const y = ((fullstopRect.top + dotHeight / 2 - circleRect.top) / circleRect.height) * 100;

            const viewportWidth = getViewportWidth();
            const viewportHeight = getViewportHeight();

            const circleCenterX = circleRect.left + (circleRect.width * x / 100);
            const circleCenterY = circleRect.top + (circleRect.height * y / 100);

            const distances = [
                Math.hypot(circleCenterX, circleCenterY),
                Math.hypot(circleCenterX - viewportWidth, circleCenterY),
                Math.hypot(circleCenterX, circleCenterY - viewportHeight),
                Math.hypot(circleCenterX - viewportWidth, circleCenterY - viewportHeight)
            ];

            const maxDistance = Math.max(...distances);
            return Math.ceil(maxDistance + 10);
        };

        const setClipPathPosition = () => {
            if (!fullstop) return;

            const circleRect = openingStatementCircle.getBoundingClientRect();
            const fullstopRect = fullstop.getBoundingClientRect();
            const x = ((fullstopRect.left + fullstopRect.width / 2 - circleRect.left) / circleRect.width) * 100;
            const y = ((fullstopRect.top + fullstopRect.height / 2 - circleRect.top) / circleRect.height) * 100;

            openingStatementCircle.style.clipPath = `circle(var(--radius) at ${x}% ${y}%)`;
        };

        setClipPathPosition();

        // Listen to window load and request animation frame
        window.addEventListener('load', setClipPathPosition, { once: true });
        requestAnimationFrame(setClipPathPosition);

        // Dedicated resize handler with proper cleanup
        let resizeTimeout;
        const handleOpeningStatementResize = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                setClipPathPosition();
                ScrollTrigger.refresh();
            }, CONFIG.RESIZE.DEBOUNCE_MS);
        };

        window.addEventListener('resize', handleOpeningStatementResize);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', handleOpeningStatementResize);
        }

        // Create the scroll-triggered animation
        gsap.fromTo(openingStatementCircle, {
            '--radius': () => getInitialRadius(),
        }, {
            "--radius": 0,
            ease: 'expo',
            scrollTrigger: {
                trigger: ".h-opening-statement",
                start: "top top",
                end: "bottom top",
                scrub: true,
                pin: true,
                pinSpacing: true,
                anticipatePin: 1,
                invalidateOnRefresh: true,
            }
        });

        // Cleanup function
        cleanupOpeningStatement = () => {
            window.removeEventListener('resize', handleOpeningStatementResize);
            if (window.visualViewport) {
                window.visualViewport.removeEventListener('resize', handleOpeningStatementResize);
            }
        };
    }

    // REUSABLE ANIMATION PATTERNS

    // Create fade-in animations for elements
    const initFadeAnimations = () => {
        gsap.utils.toArray('.editorial').forEach(editorial => {
            gsap.to(editorial, {
                opacity: 1,
                ease: 'expo.out',
                duration: CONFIG.ANIMATION.FADE_DURATION,
                scrollTrigger: {
                    trigger: editorial,
                    start: CONFIG.SCROLL_TRIGGER.FADE_START,
                    end: CONFIG.SCROLL_TRIGGER.FADE_END,
                    toggleActions: 'play none none none'
                }
            });
        });
    };

    // Create staggered animations for child elements
    const initStaggeredAnimations = (parentSelector, childSelector) => {
        gsap.utils.toArray(parentSelector).forEach(container => {
            gsap.from(container.querySelectorAll(childSelector), {
                opacity: 0,
                y: 40,
                stagger: CONFIG.ANIMATION.STAGGER_DURATION,
                duration: CONFIG.ANIMATION.STAGGER_ITEM_DURATION,
                ease: 'power2.out',
                scrollTrigger: {
                    trigger: container,
                    start: CONFIG.SCROLL_TRIGGER.FADE_START,
                    once: true
                }
            });
        });
    };

    // Respect prefers-reduced-motion for animations
    const initWithMediaQuery = (callback) => {
        const mm = gsap.matchMedia();
        mm.add('(prefers-reduced-motion: no-preference)', callback);
        return mm;
    };

    // Initialize fade animations
    initFadeAnimations();

    // Initialize staggered animations for accordions and team members
    initStaggeredAnimations('.accordions', '.accordion');
    initStaggeredAnimations('.team', '.team-member');

    // CTA AND PARALLAX ANIMATIONS

    // Handle CTA in-view state toggling
    const initCTAAnimation = () => {
        gsap.utils.toArray('.b-cta').forEach(el => {
            ScrollTrigger.create({
                trigger: el,
                start: 'top center',
                end: 'bottom center',
                onEnter: () => el.classList.add('b-cta--in-view'),
                onLeave: () => el.classList.remove('b-cta--in-view'),
                onLeaveBack: () => el.classList.remove('b-cta--in-view'),
                onEnterBack: () => el.classList.add('b-cta--in-view')
            });
        });
    };

    // Handle parallax header image
    const initParallaxHeader = () => {
        const headerImage = document.querySelector('.p-header__image');
        if (!headerImage) return;

        gsap.to(headerImage, {
            y: -50,
            scale: 1.2,
            ease: 'none',
            scrollTrigger: {
                trigger: headerImage,
                start: 'top center',
                end: 'bottom top',
                scrub: true
            }
        });
    };

    initCTAAnimation();
    initParallaxHeader();

    // CUSTOM CURSOR MANAGEMENT

    // Custom cursor manager for non-touch devices
    // Properly handles event listeners and cleanup
    class CustomCursor {
        constructor() {
            this.cursor = null;
            this.cursorText = null;
            this.lastMouse = { x: getViewportWidth() / 2, y: getViewportHeight() / 2 };
            this.handlers = new Map();
            this.isTouchDevice = ('ontouchstart' in window || navigator.maxTouchPoints);

            if (!this.isTouchDevice) {
                this.init();
            }
        }

        init() {
            this.createCursorElement();
            this.setupGlobalListeners();
            this.setupElementListeners();
        }

        createCursorElement() {
            // Remove existing cursor if present
            const existing = document.getElementById('custom-cursor');
            if (existing) existing.remove();

            const cursor = document.createElement('div');
            cursor.id = 'custom-cursor';
            cursor.innerHTML = '<span id="custom-cursor-text"></span>';
            document.body.appendChild(cursor);

            this.cursor = cursor;
            this.cursorText = cursor.querySelector('#custom-cursor-text');

            Object.assign(cursor.style, {
                position: 'fixed',
                top: '0', left: '0',
                width: '60px', height: '60px',
                borderRadius: '50%',
                background: 'var(--colour-secondary)',
                color: 'var(--colour-font-positive)',
                pointerEvents: 'none',
                zIndex: '9999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
                transform: 'translate(-50%, -50%) scale(' + CONFIG.ANIMATION.CURSOR_SCALE + ')',
                opacity: '0',
                transition: 'background 0.2s',
                willChange: 'transform, opacity'
            });

            gsap.set(cursor, {
                x: this.lastMouse.x + 40,
                y: this.lastMouse.y + 40
            });
        }

        setupGlobalListeners() {
            const handleMouseMove = (e) => {
                this.lastMouse.x = e.clientX;
                this.lastMouse.y = e.clientY;
                gsap.to(this.cursor, {
                    x: this.lastMouse.x + 40,
                    y: this.lastMouse.y + 40,
                    duration: 0.2,
                    ease: 'power3.out'
                });
            };

            const handleMouseLeave = () => {
                this.cursorText.textContent = '';
                gsap.to(this.cursor, {
                    opacity: 0,
                    scale: CONFIG.ANIMATION.CURSOR_SCALE,
                    duration: CONFIG.ANIMATION.CURSOR_SHOW_DURATION,
                    ease: 'power2.in'
                });
                document.body.style.cursor = '';
            };

            const handleHTMXBeforeRequest = () => {
                if (this.cursor) {
                    this.cursor.style.opacity = '0';
                    this.cursorText.textContent = '';
                }
                document.body.style.cursor = '';
            };

            const handleHTMXAfterSwap = () => {
                // Reset cursor state completely before re-attaching listeners
                if (this.cursor) {
                    this.cursorText.textContent = '';
                    this.cursor.style.opacity = '0';
                    document.body.style.cursor = '';
                }
                this.setupElementListeners(); // Re-attach listeners to new elements
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseleave', handleMouseLeave);
            document.addEventListener('htmx:beforeRequest', handleHTMXBeforeRequest);
            document.addEventListener('htmx:afterSwap', handleHTMXAfterSwap);

            this.handlers.set('mousemove', handleMouseMove);
            this.handlers.set('mouseleave', handleMouseLeave);
            this.handlers.set('htmx:beforeRequest', handleHTMXBeforeRequest);
            this.handlers.set('htmx:afterSwap', handleHTMXAfterSwap);
        }

        setupElementListeners() {
            // Remove old element listeners
            this.clearElementListeners();

            document.querySelectorAll('[data-cursor]').forEach(el => {
                const handleMouseEnter = () => {
                    this.cursorText.textContent = el.getAttribute('data-cursor');
                    gsap.to(this.cursor, {
                        opacity: 1,
                        scale: 1,
                        duration: CONFIG.ANIMATION.CURSOR_SHOW_DURATION,
                        ease: 'power2.out'
                    });
                    document.body.style.cursor = 'none';
                };

                const handleMouseLeave = () => {
                    this.cursorText.textContent = '';
                    gsap.to(this.cursor, {
                        opacity: 0,
                        scale: CONFIG.ANIMATION.CURSOR_SCALE,
                        duration: CONFIG.ANIMATION.CURSOR_SHOW_DURATION,
                        ease: 'power2.in'
                    });
                    document.body.style.cursor = '';
                };

                el.addEventListener('mouseenter', handleMouseEnter);
                el.addEventListener('mouseleave', handleMouseLeave);

                // Store references for cleanup
                if (!this.handlers.has(el)) {
                    this.handlers.set(el, { enter: handleMouseEnter, leave: handleMouseLeave });
                }
            });
        }

        clearElementListeners() {
            document.querySelectorAll('[data-cursor]').forEach(el => {
                const handlers = this.handlers.get(el);
                if (handlers) {
                    el.removeEventListener('mouseenter', handlers.enter);
                    el.removeEventListener('mouseleave', handlers.leave);
                    this.handlers.delete(el);
                }
            });
        }

        kill() {
            // Remove global listeners
            this.handlers.forEach((handler, key) => {
                if (typeof key === 'string') {
                    document.removeEventListener(key, handler);
                }
            });

            // Remove element listeners
            this.clearElementListeners();

            // Remove cursor element
            if (this.cursor) {
                this.cursor.remove();
                this.cursor = null;
                this.cursorText = null;
            }

            this.handlers.clear();
        }
    }

    // Only initialize custom cursor on non-touch devices
    let customCursor = null;
    if (!('ontouchstart' in window || navigator.maxTouchPoints)) {
        customCursor = new CustomCursor();
    }

    // SHOWCASE HORIZONTAL SCROLL

    // Initialize horizontal scroll showcase with progress dots
    const initShowcase = () => {
        const sticky = document.getElementById('showcase__sticky');
        const track = document.getElementById('showcase__track');
        const dotsEl = document.getElementById('progress-dots');

        if (!sticky || !track || !dotsEl) return null;

        // Derive everything from the DOM — works for any number of items
        const cards = Array.from(track.querySelectorAll('.showcase-card'));
        const totalCards = cards.length;
        const GAP = parseFloat(getComputedStyle(track).gap) || CONFIG.SCROLL_TRIGGER.SHOWCASE_GAP;

        // Build progress dots dynamically
        dotsEl.innerHTML = ''; // Clear existing
        cards.forEach(() => {
            const dot = document.createElement('div');
            dot.className = 'dot';
            dotsEl.appendChild(dot);
        });
        const dots = Array.from(dotsEl.querySelectorAll('.dot'));

        // Functional values — re-evaluated by invalidateOnRefresh on resize
        const getCardWidth = () => cards[0].offsetWidth;
        const getStartX = () => 0;
        const getEndX = () => sticky.offsetWidth - getCardWidth() - (getCardWidth() + GAP) * (totalCards - 1);
        const getSlideTotal = () => (getCardWidth() + GAP) * (totalCards - 1);

        // Respect prefers-reduced-motion
        const mm = gsap.matchMedia();

        mm.add('(prefers-reduced-motion: no-preference)', () => {
            gsap.set(track, { x: getStartX() });

            ScrollTrigger.addEventListener('refreshInit', () => {
                gsap.set(track, { x: getStartX() });
            });

            gsap.to(track, {
                x: getEndX,
                ease: 'power1.out',
                scrollTrigger: {
                    trigger: sticky,
                    start: 'center center',
                    end: () => `+=${getSlideTotal()}`,
                    pin: true,
                    scrub: 1,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onEnter: () => dotsEl.classList.add('visible'),
                    onLeave: () => dotsEl.classList.remove('visible'),
                    onEnterBack: () => dotsEl.classList.add('visible'),
                    onLeaveBack: () => dotsEl.classList.remove('visible'),
                    onUpdate(self) {
                        const active = Math.round(self.progress * (totalCards - 1));
                        dots.forEach((d, i) => d.classList.toggle('active', i === active));
                    },
                },
            });

            // Refresh after showcase animation is fully set up
            setTimeout(() => ScrollTrigger.refresh(), CONFIG.RESIZE.SCROLL_REFRESH_MS);
        });

        mm.add('(prefers-reduced-motion: reduce)', () => {
            gsap.set(track, { x: getStartX() });
            setTimeout(() => ScrollTrigger.refresh(), CONFIG.RESIZE.SCROLL_REFRESH_MS);
        });

        return mm;
    };

    const showcaseMediaQuery = initShowcase();

    // FOOTER ICON ANIMATION

    // Animate footer icon at bottom of page
    const initFooterIcon = () => {
        const siteFooterIcon = document.querySelector('.footer-icon');
        if (!siteFooterIcon) return;

        ScrollTrigger.create({
            trigger: siteFooterIcon,
            start: 'bottom bottom',
            end: 'bottom top',
            toggleClass: 'footer-icon--play'
        });
    };

    initFooterIcon();

    // HTMX LIFECYCLE

    // Refresh ScrollTrigger and re-initialize animations after HTMX swap
    // This is safer than aggressive cleanup
    document.addEventListener('htmx:afterSettle', () => {
        // Kill old menu manager and create new one (menu elements may have changed)
        if (menuManager) {
            menuManager.kill();
        }
        menuManager = new MenuManager();

        // Refresh ScrollTrigger to account for new DOM
        ScrollTrigger.refresh();

        // Re-initialize animations for newly swapped content
        setTimeout(() => {
            initFadeAnimations();
            initStaggeredAnimations('.accordions', '.accordion');
            initStaggeredAnimations('.team', '.team-member');
            initCTAAnimation();
            initParallaxHeader();
            initHomepageHeaderSnap();
            initFooterIcon();
        }, 50);

        // Re-setup custom cursor element listeners for new elements
        if (customCursor) {
            customCursor.setupElementListeners();
        }
    });

});